// Raw IndexedDB access for the Issues local replica.
//
// This deliberately does NOT reuse ScSnapshotStore. That class holds one envelope
// record containing a whole array, keyed by the caller's scope. Two consequences
// make it unusable here: every write rewrites the entire array, and changing scope
// starts a different key so the rows the user just left stop being reachable. This
// store holds one record per issue, keyed by the issue id. Widening or narrowing
// the working scope changes only which ids a scope tracks in `syncState`; it never
// rewrites a row and never discards row data. Switching scope must stay cheap.
//
// Local state is a cache and never a source of truth. Every read path treats an
// absent database, an absent store and a failed request as "nothing cached", which
// makes the engine fall back to a full re-sync. That is a routine outcome, not an
// error: iOS clears storage after seven days without interaction and evicts
// origins used irregularly, so a tablet used weekly starts empty most shifts.

// One database per tenant. A tenant switch therefore lands on a different database
// and cannot read the previous tenant's rows, which keeps rows keyed by issue id
// alone inside a tenant. Purging is a separate explicit act — partitioning is what
// makes the switch safe, because some tenant changes come from a route guard that
// runs no teardown code at all.
// Shared with the sign-out purge, which has to find the databases of tenants this session
// never opened and so matches on the prefix rather than on a name it can build.
export const ISSUE_DB_PREFIX = 'fleethd-issues-';

export function issueSyncDbName(externalTenantId: string): string {
  return `${ISSUE_DB_PREFIX}${externalTenantId || 'anon'}`;
}

export const ROWS_STORE = 'rows';
export const SYNC_STATE_STORE = 'syncState';
const TOUCHED_AT_INDEX = 'byTouchedAt';
const DB_VERSION = 1;

// A row is stored inside an envelope rather than on its own. The server row is kept
// exactly as received — no reshaping on ingest — and the envelope carries the two
// things the client owns: the key it is filed under, and the recency the residency
// cap evicts by.
export interface RowEnvelope<TRow> {
  issueId: string;
  row: TRow;
  // Last time a delta or a reconciliation confirmed this row is present in a
  // synced scope. Eviction removes the coldest first, so rows belonging to a scope
  // the user has stopped looking at go before rows in the active one. Read
  // recency is useless for this: a query layer scan touches every resident row.
  touchedAtMs: number;
}

export interface StoredSyncState {
  scopeKey: string;
  // Stored exactly as the server returned it. A cursor is opaque: deriving one
  // from row contents would skip rows whenever writer clocks disagree.
  nextCursor: string;
  scopeVersion: string;
  complete: boolean;
  scopeTotal: number;
  scopeTotalIsFloor: boolean;
  asOfUtc: string;
  lastReconciledAtMs: number;
  // The descriptor the cursor belongs to, so a scope can be recognised again
  // without re-deriving its key.
  scope: string;
  fingerprint: string;
}

export function isQuotaExceeded(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  // Chrome and Safari report the modern name; older WebKit still throws the
  // legacy one, and Firefox has used a bare DOMException for a full disk.
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED';
}

// Persistence for the local replica. All methods degrade to a no-op or an empty
// result when IndexedDB is unavailable, so no user path can fail because a cache
// write failed.
export class IssueSyncDb<TRow> {
  private dbPromise: Promise<IDBDatabase | null> | null = null;

  // Set by close(), and never by a failure. Closing has to be final: the tenant swap
  // closes the outgoing database while work started under the previous tenant may still
  // be in flight, and a reopen would let that work write the new tenant's rows into the
  // old tenant's database. Nulling the connection alone does not stop it, because the
  // next read opens a fresh one.
  private closed = false;

  // The factory is the browser's IndexedDB. Unit tests pass an in-memory one, so they never
  // wait on the disk.
  constructor(
    private readonly dbName: string,
    private readonly factory: IDBFactory | null = typeof indexedDB === 'undefined' ? null : indexedDB,
  ) {}

  async getSyncState(scopeKey: string): Promise<StoredSyncState | null> {
    const db = await this.db();
    if (!db) return null;
    const found = await this.read<StoredSyncState | undefined>(
      db, SYNC_STATE_STORE, store => store.get(scopeKey),
    );
    return found ?? null;
  }

  async getAllSyncState(): Promise<StoredSyncState[]> {
    const db = await this.db();
    if (!db) return [];
    const found = await this.read<StoredSyncState[] | undefined>(
      db, SYNC_STATE_STORE, store => store.getAll(),
    );
    return found ?? [];
  }

  async putSyncState(state: StoredSyncState): Promise<void> {
    const db = await this.db();
    if (!db) return;
    await this.write(db, SYNC_STATE_STORE, store => { store.put(state); });
  }

  async deleteSyncState(scopeKey: string): Promise<void> {
    const db = await this.db();
    if (!db) return;
    await this.write(db, SYNC_STATE_STORE, store => { store.delete(scopeKey); });
  }

  async getRow(issueId: string): Promise<RowEnvelope<TRow> | null> {
    const db = await this.db();
    if (!db) return null;
    const found = await this.read<RowEnvelope<TRow> | undefined>(
      db, ROWS_STORE, store => store.get(issueId),
    );
    return found ?? null;
  }

  // Reads a named set of rows in one transaction. The cost tracks the size of the set
  // rather than the size of the store, which is what lets a delta be applied without
  // re-reading everything the client holds.
  async getRows(issueIds: string[]): Promise<RowEnvelope<TRow>[]> {
    if (!issueIds.length) return [];
    const db = await this.db();
    if (!db) return [];
    return new Promise<RowEnvelope<TRow>[]>(resolve => {
      const found: RowEnvelope<TRow>[] = [];
      let settled = false;
      const done = () => { if (!settled) { settled = true; resolve(found); } };
      try {
        const tx = db.transaction(ROWS_STORE, 'readonly');
        const store = tx.objectStore(ROWS_STORE);
        for (const id of issueIds) {
          const request = store.get(id);
          request.onsuccess = () => {
            const envelope = request.result as RowEnvelope<TRow> | undefined;
            if (envelope) found.push(envelope);
          };
        }
        tx.oncomplete = done;
        tx.onabort = done;
        tx.onerror = done;
      } catch {
        done();
      }
    });
  }

  async getAllRows(): Promise<RowEnvelope<TRow>[]> {
    const db = await this.db();
    if (!db) return [];
    const found = await this.read<RowEnvelope<TRow>[] | undefined>(
      db, ROWS_STORE, store => store.getAll(),
    );
    return found ?? [];
  }

  async getAllRowIds(): Promise<string[]> {
    const db = await this.db();
    if (!db) return [];
    const found = await this.read<IDBValidKey[] | undefined>(
      db, ROWS_STORE, store => store.getAllKeys(),
    );
    return (found ?? []).map(key => String(key));
  }

  async countRows(): Promise<number> {
    const db = await this.db();
    if (!db) return 0;
    const found = await this.read<number | undefined>(db, ROWS_STORE, store => store.count());
    return found ?? 0;
  }

  // Upserts and evictions for one delta, in a single transaction so a partly
  // applied delta can never be paired with an advanced cursor. The cursor write
  // shares this transaction for the same reason.
  async applyBatch(
    upserts: RowEnvelope<TRow>[],
    removedIds: string[],
    state: StoredSyncState | null,
  ): Promise<void> {
    const db = await this.db();
    if (!db) return;
    await this.writeMany(db, [ROWS_STORE, SYNC_STATE_STORE], tx => {
      const rows = tx.objectStore(ROWS_STORE);
      for (const envelope of upserts) rows.put(envelope);
      for (const id of removedIds) rows.delete(id);
      if (state) tx.objectStore(SYNC_STATE_STORE).put(state);
    });
  }

  async deleteRows(issueIds: string[]): Promise<void> {
    if (!issueIds.length) return;
    const db = await this.db();
    if (!db) return;
    await this.write(db, ROWS_STORE, store => {
      for (const id of issueIds) store.delete(id);
    });
  }

  async touchRows(issueIds: string[], atMs: number): Promise<void> {
    if (!issueIds.length) return;
    const db = await this.db();
    if (!db) return;
    await this.writeMany(db, [ROWS_STORE], tx => {
      const rows = tx.objectStore(ROWS_STORE);
      for (const id of issueIds) {
        const get = rows.get(id);
        get.onsuccess = () => {
          const found = get.result as RowEnvelope<TRow> | undefined;
          if (found) rows.put({ ...found, touchedAtMs: atMs });
        };
      }
    });
  }

  // The coldest `count` ids, for the residency cap. Walks the recency index rather
  // than loading and sorting every row, so eviction cost tracks what it removes.
  async coldestRowIds(count: number): Promise<string[]> {
    if (count <= 0) return [];
    const db = await this.db();
    if (!db) return [];
    return new Promise<string[]>(resolve => {
      const ids: string[] = [];
      let settled = false;
      const done = () => { if (!settled) { settled = true; resolve(ids); } };
      try {
        const tx = db.transaction(ROWS_STORE, 'readonly');
        const cursorRequest = tx.objectStore(ROWS_STORE)
          .index(TOUCHED_AT_INDEX).openKeyCursor(null, 'next');
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (!cursor || ids.length >= count) return done();
          ids.push(String(cursor.primaryKey));
          cursor.continue();
        };
        cursorRequest.onerror = done;
        tx.onabort = done;
        tx.oncomplete = done;
      } catch {
        done();
      }
    });
  }

  async clear(): Promise<void> {
    const db = await this.db();
    if (!db) return;
    await this.writeMany(db, [ROWS_STORE, SYNC_STATE_STORE], tx => {
      tx.objectStore(ROWS_STORE).clear();
      tx.objectStore(SYNC_STATE_STORE).clear();
    });
  }

  // Drops the whole database. Used to purge a tenant rather than to recover from a
  // failure — recovery is a re-sync into the same database.
  async destroy(): Promise<void> {
    this.close();
    const factory = this.factory;
    if (!factory) return;
    await new Promise<void>(resolve => {
      const request = factory.deleteDatabase(this.dbName);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      // Another tab still holds a connection. The delete completes when that tab
      // closes; nothing here should wait for it.
      request.onblocked = () => resolve();
    });
    // The database is gone rather than handed off, so the instance may open a fresh one
    // and sync into it. This is the one path that lifts the close fence.
    this.closed = false;
  }

  close(): void {
    this.closed = true;
    const pending = this.dbPromise;
    this.dbPromise = null;
    if (!pending) return;
    pending.then(db => db?.close()).catch(() => undefined);
  }

  private read<R>(
    db: IDBDatabase,
    storeName: string,
    run: (store: IDBObjectStore) => IDBRequest,
  ): Promise<R | undefined> {
    return new Promise<R | undefined>(resolve => {
      try {
        const request = run(db.transaction(storeName, 'readonly').objectStore(storeName));
        request.onsuccess = () => resolve(request.result as R);
        request.onerror = () => resolve(undefined);
      } catch {
        // A missing store throws NotFoundError synchronously. That is the evicted
        // case, and it reads as "nothing cached".
        resolve(undefined);
      }
    });
  }

  private write(
    db: IDBDatabase,
    storeName: string,
    run: (store: IDBObjectStore) => void,
  ): Promise<void> {
    return this.writeMany(db, [storeName], tx => run(tx.objectStore(storeName)));
  }

  // Resolves on `oncomplete`, not on the last request's `onsuccess`. A request can
  // succeed and the transaction still abort — a full disk aborts it — so treating a
  // request success as durable would advance a cursor over rows that were never
  // stored. A quota failure surfaces to the caller, which frees space and retries; this
  // never assumes the quota.
  private writeMany(
    db: IDBDatabase,
    storeNames: string[],
    run: (tx: IDBTransaction) => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let tx: IDBTransaction;
      try {
        tx = db.transaction(storeNames, 'readwrite');
      } catch (err) {
        return reject(err);
      }
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error);
      tx.onerror = () => reject(tx.error);
      try {
        run(tx);
      } catch (err) {
        try { tx.abort(); } catch { /* already aborting */ }
        reject(err);
      }
    });
  }

  private db(): Promise<IDBDatabase | null> {
    if (this.closed) return Promise.resolve(null);
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise<IDBDatabase | null>(resolve => {
      if (!this.factory) return resolve(null);
      let open: IDBOpenDBRequest;
      try {
        open = this.factory.open(this.dbName, DB_VERSION);
      } catch {
        return resolve(null);
      }
      open.onupgradeneeded = () => {
        const db = open.result;
        // Create only. Never delete an existing store here: that is how a schema
        // change would silently destroy rows a running tab still believes it holds.
        if (!db.objectStoreNames.contains(ROWS_STORE)) {
          const rows = db.createObjectStore(ROWS_STORE, { keyPath: 'issueId' });
          rows.createIndex(TOUCHED_AT_INDEX, 'touchedAtMs', { unique: false });
        }
        if (!db.objectStoreNames.contains(SYNC_STATE_STORE)) {
          db.createObjectStore(SYNC_STATE_STORE, { keyPath: 'scopeKey' });
        }
      };
      open.onsuccess = () => {
        const db = open.result;
        // Close on another tab's upgrade or delete, otherwise this connection
        // blocks it indefinitely and that tab hangs with no error.
        db.onversionchange = () => { this.dbPromise = null; db.close(); };
        resolve(db);
      };
      open.onerror = () => resolve(null);
      open.onblocked = () => resolve(null);
    });
    return this.dbPromise;
  }
}
